import { useEffect, useMemo, useRef, useState } from 'react'

// Dependency-free SVG line chart. Generic over an arbitrary list of series
// (each {key,label,color,dashed?}); every data row carries `label`, `full`
// (tooltip title) and one numeric value per series key. Responsive width,
// smoothed lines, hover guideline + tooltip, optional on-point value labels.

// 1,234,567 → "1.2M" / "45k" / "820"  (no ฿ — labels show the bare number).
function fmtShort(n) {
  const v = Number(n) || 0
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 1_000)}k`
  return `${Math.round(v)}`
}
function fmtFull(n) {
  return Math.round(Number(n) || 0).toLocaleString('en-US')
}
// on-point data labels: the whole number, rounded down, no decimals and no k/M
// suffix (e.g. 8,234,567 — never "8M").
function fmtPoint(n) {
  return Math.floor(Number(n) || 0).toLocaleString('en-US')
}

function niceMax(max) {
  if (max <= 0) return 1000
  const pow = Math.pow(10, Math.floor(Math.log10(max)))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s * 5 >= max) || 10 * pow
  return Math.ceil(max / step) * step
}

// Catmull-Rom → cubic-bézier smoothing. Low tension = a gentle curve.
function smoothPath(p, tension = 0.16) {
  if (p.length < 2) return p.length ? `M${p[0].x},${p[0].y}` : ''
  let d = `M${p[0].x.toFixed(1)},${p[0].y.toFixed(1)}`
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[i - 1] || p[i], p1 = p[i], p2 = p[i + 1], p3 = p[i + 2] || p2
    const c1x = p1.x + (p2.x - p0.x) * tension, c1y = p1.y + (p2.y - p0.y) * tension
    const c2x = p2.x - (p3.x - p1.x) * tension, c2y = p2.y - (p3.y - p1.y) * tension
    d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`
  }
  return d
}

export default function RevenueChart({ data = [], series = [], height = 300, valueLabels, showLegend = true, compact = true, labelAll = false, emptyText = 'ไม่มีข้อมูล' }) {
  const fmtLabel = compact ? fmtShort : fmtFull   // axis + on-point labels (tooltip always full)
  const wrapRef = useRef(null)
  const [width, setWidth] = useState(720)
  const [hover, setHover] = useState(null)
  const showVals = valueLabels ?? series.length === 1

  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width
      if (w) setWidth(Math.max(320, Math.round(w)))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const M = { top: 16, right: 18, bottom: 38, left: compact ? 54 : 76 }
  const plotW = Math.max(10, width - M.left - M.right)
  const plotH = Math.max(10, height - M.top - M.bottom)

  const { yMax, ticks, pts } = useMemo(() => {
    const peak = data.reduce((m, row) => series.reduce((mm, s) => Math.max(mm, row[s.key] || 0), m), 0)
    const yMax = niceMax(peak)
    const ticks = Array.from({ length: 6 }, (_, i) => (yMax / 5) * i)
    const n = data.length
    const x = (i) => (n <= 1 ? plotW / 2 : (plotW * i) / (n - 1))
    const y = (v) => plotH - (plotH * (v || 0)) / yMax
    const pts = series.map((s) => {
      const dots = data.map((row, i) => ({ cx: x(i), cy: y(row[s.key]), v: row[s.key] }))
      return { ...s, d: smoothPath(dots.map((p) => ({ x: p.cx, y: p.cy }))), dots }
    })
    return { yMax, ticks, pts }
  }, [data, series, plotW, plotH])

  const xAt = (i) => (data.length <= 1 ? plotW / 2 : (plotW * i) / (data.length - 1))
  const labelStep = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(plotW / 64))))
  // labelAll only forces step=1 for a single series; multiple series always use density calc
  // (wider gap per label when stacking 2+ values per x-position to avoid overlap)
  const valuePx = series.length > 1 ? 112 : 80
  const valueStep = labelAll && series.length === 1 ? 1 : Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(plotW / valuePx))))

  function onMove(e) {
    if (!data.length) return
    const rect = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - rect.left) / rect.width) * width - M.left
    const i = data.length <= 1 ? 0 : Math.round((px / plotW) * (data.length - 1))
    setHover(Math.min(data.length - 1, Math.max(0, i)))
  }

  if (!data.length || !series.length) {
    return <div className="flex items-center justify-center text-[13px] text-slate-400" style={{ height }}>{emptyText}</div>
  }

  const hv = hover != null ? data[hover] : null
  const hvX = hover != null ? xAt(hover) : 0

  // tooltip entries — non-empty series at the hovered x, biggest first, capped.
  const tipEntries = hv
    ? series.map((s) => ({ label: s.label, color: s.color, val: hv[s.key] }))
        .filter((e) => e.val != null && (series.length <= 2 || e.val > 0))
        .sort((a, b) => (b.val || 0) - (a.val || 0))
        .slice(0, 10)
    : []
  const tipH = 22 + tipEntries.length * 15 + 6

  return (
    <div ref={wrapRef} className="w-full">
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} overflow="visible" onMouseMove={onMove} onMouseLeave={() => setHover(null)} className="overflow-visible select-none">
        <g transform={`translate(${M.left},${M.top})`}>
          {ticks.map((t, i) => {
            const gy = plotH - (plotH * t) / yMax
            return (
              <g key={i}>
                <line x1={0} y1={gy} x2={plotW} y2={gy} stroke="#eef2f6" strokeWidth={1} />
                <text x={-10} y={gy} dy="0.32em" textAnchor="end" className="fill-slate-400" fontSize={10.5}>{fmtLabel(t)}</text>
              </g>
            )
          })}

          {hv && <line x1={hvX} y1={0} x2={hvX} y2={plotH} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="3 3" />}

          {pts.map((s) => (
            <path key={s.key} d={s.d} fill="none" stroke={s.color} strokeWidth={2}
              strokeDasharray={s.dashed ? '5 4' : undefined} strokeLinejoin="round" strokeLinecap="round" />
          ))}

          {/* on-point value labels (only when ≤2 series): 1st above, 2nd below */}
          {showVals && data.map((row, i) => {
            if (i % valueStep !== 0 && i !== data.length - 1) return null
            return (
              <g key={`v${i}`} fontSize={9.5} fontWeight="600" textAnchor="middle">
                {pts.map((s, si) =>
                  row[s.key] > 0 ? (
                    <text key={s.key} x={s.dots[i].cx} y={s.dots[i].cy + (si === 0 ? -8 : 14 + (si - 1) * 13)} fill={s.color}>{fmtPoint(row[s.key])}</text>
                  ) : null
                )}
              </g>
            )
          })}

          {hv && pts.map((s) => <circle key={s.key} cx={s.dots[hover].cx} cy={s.dots[hover].cy} r={3.5} fill="#fff" stroke={s.color} strokeWidth={2} />)}

          {data.map((row, i) =>
            i % labelStep === 0 || i === data.length - 1 ? (
              <text key={i} x={xAt(i)} y={plotH + 18} textAnchor="middle" className="fill-slate-400" fontSize={10.5}>{row.label}</text>
            ) : null
          )}
        </g>

        {hv && tipEntries.length > 0 && (() => {
          const tipW = 184
          const tx = Math.min(width - tipW - 4, Math.max(4, M.left + hvX - tipW / 2))
          return (
            <g transform={`translate(${tx},${M.top + 4})`}>
              <rect width={tipW} height={tipH} rx={8} fill="#0f172a" opacity={0.94} />
              <text x={12} y={17} className="fill-white" fontSize={11} fontWeight="600">{hv.full || hv.label}</text>
              {tipEntries.map((e, i) => (
                <g key={i} transform={`translate(12,${33 + i * 15})`}>
                  <circle cx={3} cy={-3} r={3.5} fill={e.color} />
                  <text x={14} y={0} className="fill-slate-300" fontSize={10}>{e.label}</text>
                  <text x={tipW - 24} y={0} textAnchor="end" className="fill-white" fontSize={10} fontWeight="600">{fmtFull(e.val)}</text>
                </g>
              ))}
            </g>
          )
        })()}
      </svg>

      {showLegend && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 justify-center mt-1">
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5 text-[12px] text-slate-600">
              <span className="w-3 h-[3px] rounded-full" style={{ background: s.color, ...(s.dashed ? { backgroundImage: 'none', borderTop: `2px dashed ${s.color}`, height: 0, width: 14 } : null) }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
